import { useMemo, useState } from 'react';

import {
	DOMAINS_SECTIONS,
	OPERATIONS_SECTIONS,
	PROGRAM_SECTIONS,
	SETTINGS_GROUPS,
} from '@ekhodealer/ekho-common';
import { Tabs } from '@ekhodealer/ekho-common/rev';

import type { Section } from '@ekhodealer/ekho-common';

import { SettingsPageContent, type TocItem } from '@common/components/SettingsTableOfContents';

import { useCombinedServiceSections } from '../../hooks/useServiceProgramInfo';
import { getSectionGroup } from '../../lib/sectionGroupUtils';

import { SectionContent } from './SectionContent';

import './ConfigMgmt.scss';

/**
 * Display names for each section used in tabs.
 */
const SECTION_DISPLAY_NAMES: Partial<Record<Section, string>> = {
	[PROGRAM_SECTIONS.CHECKOUT]: 'Checkout',
	[PROGRAM_SECTIONS.PREORDER]: 'Preorder',
	[DOMAINS_SECTIONS.SHOP]: 'Shop',
	[DOMAINS_SECTIONS.SUBDOMAINS]: 'Subdomains',
	[DOMAINS_SECTIONS.CRM]: 'CRM',
	[OPERATIONS_SECTIONS.SALE]: 'Sale',
	[OPERATIONS_SECTIONS.DELIVERY]: 'Delivery',
	[OPERATIONS_SECTIONS.PICKUP]: 'Pickup',
	[OPERATIONS_SECTIONS.ASSEMBLY]: 'Assembly',
	[OPERATIONS_SECTIONS.INSPECTION]: 'Inspection',
	[OPERATIONS_SECTIONS.FREIGHT]: 'Freight',
	[OPERATIONS_SECTIONS.TITLING]: 'Titling',
	[OPERATIONS_SECTIONS.REGISTRATION]: 'Registration',
	[OPERATIONS_SECTIONS.COVERAGE_FULFILLMENT]: 'Coverage Fulfillment',
	[OPERATIONS_SECTIONS.FINANCING_BROKERAGE]: 'Financing Brokerage',
	[OPERATIONS_SECTIONS.TARIFF]: 'Tariff',
	[OPERATIONS_SECTIONS.PROCESSING_FEES]: 'Processing Fees',
	[OPERATIONS_SECTIONS.TEMP_TAG]: 'Temp Tag',
};

/**
 * Derive a fallback display name from a section ID.
 */
function getSectionDisplayName(section: Section): string {
	if (SECTION_DISPLAY_NAMES[section]) {
		return SECTION_DISPLAY_NAMES[section]!;
	}
	const withoutPrefix = section.replace(
		/^(PROGRAM_|DOMAINS_|INTERNAL_|OPERATIONS_|INVENTORY_)/,
		'',
	);
	return withoutPrefix
		.split('_')
		.map((word) => word.charAt(0) + word.slice(1).toLowerCase())
		.join(' ');
}

/**
 * Slugify helper — must match the one in SettingsSection so TOC IDs align.
 */
function slugify(text: string): string {
	return text
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-|-$/g, '');
}

/**
 * TOC items for each section tab. IDs must match the slugified SettingsSection titles.
 */
const SECTION_TOC_ITEMS: Partial<Record<Section, TocItem[]>> = {
	[PROGRAM_SECTIONS.CHECKOUT]: [
		{ id: 'assignment', title: 'Assignment' },
		{ id: 'fulfillment-options', title: 'Fulfillment options' },
		{ id: 'payment-methods', title: 'Payment methods' },
		{ id: 'states-supported', title: 'States supported' },
		{ id: 'disclosures', title: 'Disclosures' },
	],
	[PROGRAM_SECTIONS.PREORDER]: [{ id: 'standard-preorder', title: 'Standard preorder' }],
	[OPERATIONS_SECTIONS.SALE]: [{ id: 'sale', title: 'Sale' }],
	[OPERATIONS_SECTIONS.DELIVERY]: [{ id: 'delivery', title: 'Delivery' }],
	[OPERATIONS_SECTIONS.PICKUP]: [{ id: 'pickup', title: 'Pickup' }],
	[OPERATIONS_SECTIONS.ASSEMBLY]: [{ id: 'assembly', title: 'Assembly' }],
	[OPERATIONS_SECTIONS.INSPECTION]: [{ id: 'inspection', title: 'Inspection' }],
	[OPERATIONS_SECTIONS.TITLING]: [{ id: 'titling', title: 'Titling' }],
	[OPERATIONS_SECTIONS.REGISTRATION]: [{ id: 'registration', title: 'Registration' }],
	[OPERATIONS_SECTIONS.FREIGHT]: [{ id: 'freight', title: 'Freight' }],
};

/**
 * Get TOC items for a section, falling back to a single item derived from the display name.
 */
function getTocItems(section: Section): TocItem[] {
	if (SECTION_TOC_ITEMS[section]) {
		return SECTION_TOC_ITEMS[section]!;
	}
	const title = getSectionDisplayName(section);
	return [{ id: slugify(title), title }];
}

/**
 * ConfigMgmt composes all service configuration forms together.
 * Shown under "Transaction Layer" in settings.
 * Each available section is a horizontal tab at the top.
 * Active tab's content uses the two-column SettingsSection layout.
 */
export function ConfigMgmt() {
	const combinedSections = useCombinedServiceSections();

	// Filter out Inventory sections
	const sections = useMemo(
		() =>
			combinedSections.filter((section) => {
				const group = getSectionGroup(section);
				return group && group !== SETTINGS_GROUPS.INVENTORY;
			}),
		[combinedSections],
	);

	const [activeSection, setActiveSection] = useState<Section | null>(sections[0] ?? null);

	const tabItems = sections.map((section) => ({
		id: section,
		content: getSectionDisplayName(section),
	}));

	const tocItems = useMemo(
		() => (activeSection ? getTocItems(activeSection) : []),
		[activeSection],
	);

	return (
		<div className='config-mgmt'>
			{tabItems.length > 0 && (
				<div className='config-mgmt__tabs' data-settings-tabs>
					<Tabs
						items={tabItems}
						selectedKey={activeSection}
						onSelect={(item) => setActiveSection(item.id as Section)}
					/>
				</div>
			)}
			<div className='config-mgmt__content' data-settings-tab-content>
				<SettingsPageContent tocItems={tocItems} stickyTop={64}>
					{activeSection && <SectionContent section={activeSection} />}
				</SettingsPageContent>
			</div>
		</div>
	);
}

export default ConfigMgmt;
